// ===========================================
// Flexible Pricing Matrix Tab
// ===========================================
// Editable grid for configurable dimension x Duration x Category pricing

import { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Button,
  Alert,
  CircularProgress,
  Typography,
  Chip,
  Skeleton,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useFlexiblePricingMatrix, useBulkUpdateFlexiblePricing } from '../../../hooks/usePricing';
import type { FlexibleBulkPriceUpdate, PricingCategory, PricingDimension } from '../../../api/pricing.api';

// ===========================================
// TYPES
// ===========================================

interface PriceValue {
  rowId: string;
  durationId: string;
  category: PricingCategory;
  price: string;
  originalPrice: number | null;
}

// ===========================================
// DIMENSION LABELS
// ===========================================

const DIMENSION_ROW_LABELS: Record<PricingDimension, string> = {
  PROGRAM_LEVEL: 'Program Level',
  AGE_GROUP: 'Age Group',
  INSTRUMENT: 'Instrument',
  PRICE_ONLY: 'Rate',
};

const DIMENSION_EMPTY_MESSAGES: Record<PricingDimension, string> = {
  PROGRAM_LEVEL: 'Please configure Program Levels and Lesson Durations before setting up pricing.',
  AGE_GROUP: 'Please configure Age Groups and Lesson Durations before setting up pricing.',
  INSTRUMENT: 'Please configure Instruments and Lesson Durations before setting up pricing.',
  PRICE_ONLY: 'Please configure Lesson Durations before setting up pricing.',
};

const CATEGORIES: PricingCategory[] = ['GROUP', 'INDIVIDUAL'];

// ===========================================
// PRICE INPUT COMPONENT
// ===========================================

interface PriceInputProps {
  rowId: string;
  durationId: string;
  category: PricingCategory;
  priceValues: Map<string, PriceValue>;
  onPriceChange: (rowId: string, durationId: string, category: PricingCategory, value: string) => void;
}

function PriceInput({ rowId, durationId, category, priceValues, onPriceChange }: PriceInputProps) {
  const key = `${rowId}-${durationId}-${category}`;
  const priceValue = priceValues.get(key);

  return (
    <TextField
      size="small"
      type="number"
      value={priceValue?.price ?? ''}
      onChange={(e) => onPriceChange(rowId, durationId, category, e.target.value)}
      placeholder="\u2014"
      inputProps={{
        min: 0,
        step: 0.01,
        style: { textAlign: 'center' },
      }}
      InputProps={{
        startAdornment: priceValue?.price ? (
          <InputAdornment position="start">$</InputAdornment>
        ) : null,
      }}
      sx={{
        width: 100,
        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
          display: 'none',
        },
        '& input[type=number]': {
          MozAppearance: 'textfield',
        },
      }}
    />
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function FlexiblePricingMatrixTab() {
  const { data: matrix, isLoading, error } = useFlexiblePricingMatrix();
  const bulkUpdateMutation = useBulkUpdateFlexiblePricing();

  const [priceValues, setPriceValues] = useState<Map<string, PriceValue>>(new Map());
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize price values when data loads
  useEffect(() => {
    if (matrix) {
      const newPriceValues = new Map<string, PriceValue>();

      matrix.rows.forEach((row) => {
        matrix.durations.forEach((d) => {
          CATEGORIES.forEach((category) => {
            const key = `${row.id}-${d.id}-${category}`;
            const existingPrice = matrix.prices.find(
              (p) => p.rowId === row.id && p.durationId === d.id && p.category === category
            );

            newPriceValues.set(key, {
              rowId: row.id,
              durationId: d.id,
              category,
              price: existingPrice ? existingPrice.price.toFixed(2) : '',
              originalPrice: existingPrice?.price ?? null,
            });
          });
        });
      });

      setPriceValues(newPriceValues);
      setHasChanges(false);
    }
  }, [matrix]);

  // Handle price change
  const handlePriceChange = useCallback(
    (rowId: string, durationId: string, category: PricingCategory, value: string) => {
      const key = `${rowId}-${durationId}-${category}`;
      setPriceValues((prev) => {
        const newMap = new Map(prev);
        const existing = prev.get(key);
        if (existing) {
          newMap.set(key, { ...existing, price: value });
        }
        return newMap;
      });
      setHasChanges(true);
    },
    []
  );

  // Get changed prices for saving
  const changedPrices = useMemo(() => {
    const changes: FlexibleBulkPriceUpdate[] = [];

    priceValues.forEach((value) => {
      const newPrice = parseFloat(value.price);
      if (!isNaN(newPrice) && newPrice >= 0) {
        if (value.originalPrice === null || Math.abs(newPrice - value.originalPrice) > 0.001) {
          changes.push({
            rowId: value.rowId,
            durationId: value.durationId,
            category: value.category,
            price: newPrice,
          });
        }
      }
    });

    return changes;
  }, [priceValues]);

  // Handle save
  const handleSave = async () => {
    if (changedPrices.length === 0) return;

    try {
      await bulkUpdateMutation.mutateAsync(changedPrices);
      setHasChanges(false);
    } catch {
      // Error handled by mutation
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error">
        Failed to load pricing matrix. Please try again.
      </Alert>
    );
  }

  // No data state
  const dimension = matrix?.dimension ?? 'PROGRAM_LEVEL';
  if (!matrix || matrix.rows.length === 0 || matrix.durations.length === 0) {
    return (
      <Alert severity="info">
        {DIMENSION_EMPTY_MESSAGES[dimension]}
        <br />
        Go to <strong>Class Configuration</strong> to add them.
      </Alert>
    );
  }

  const sortedRows = [...matrix.rows].sort((a, b) => a.sortOrder - b.sortOrder);
  const sortedDurations = [...matrix.durations].sort((a, b) => a.minutes - b.minutes);
  const rowLabel = DIMENSION_ROW_LABELS[dimension];

  return (
    <Box>
      {/* Header with save button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Set prices for each {rowLabel.toLowerCase()}, duration, and lesson type.
          Leave empty for combinations that don't apply.
        </Typography>
        <Button
          variant="contained"
          startIcon={bulkUpdateMutation.isPending ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={!hasChanges || changedPrices.length === 0 || bulkUpdateMutation.isPending}
        >
          {bulkUpdateMutation.isPending ? 'Saving...' : 'Save All'}
        </Button>
      </Box>

      {hasChanges && changedPrices.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have {changedPrices.length} unsaved change{changedPrices.length > 1 ? 's' : ''}.
        </Alert>
      )}

      {/* Pricing Matrix Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            {/* First Row: Duration headers spanning 2 columns each */}
            <TableRow>
              <TableCell
                rowSpan={2}
                sx={{
                  fontWeight: 'bold',
                  minWidth: 180,
                  verticalAlign: 'bottom',
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                }}
              >
                {rowLabel}
              </TableCell>
              {sortedDurations.map((duration) => (
                <TableCell
                  key={duration.id}
                  colSpan={2}
                  align="center"
                  sx={{
                    fontWeight: 'bold',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    borderLeft: '2px solid',
                    borderLeftColor: 'divider',
                  }}
                >
                  {duration.minutes} min
                  {!duration.isActive && (
                    <Chip
                      label="Inactive"
                      size="small"
                      sx={{ ml: 0.5, fontSize: '0.65rem', height: 16 }}
                    />
                  )}
                </TableCell>
              ))}
            </TableRow>

            {/* Second Row: Group | Individual sub-headers */}
            <TableRow>
              {sortedDurations.map((duration) => (
                <Fragment key={duration.id}>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                      minWidth: 100,
                      borderRight: '1px dashed',
                      borderColor: 'divider',
                      borderBottom: '2px solid',
                      borderBottomColor: 'divider',
                      borderLeft: '2px solid',
                      borderLeftColor: 'divider',
                      py: 0.5,
                    }}
                  >
                    Group
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                      minWidth: 100,
                      borderBottom: '2px solid',
                      borderBottomColor: 'divider',
                      py: 0.5,
                    }}
                  >
                    Individual
                  </TableCell>
                </Fragment>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedRows.map((row) => (
              <TableRow
                key={row.id}
                sx={{
                  opacity: row.isActive ? 1 : 0.5,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {row.color && (
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          bgcolor: row.color,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <Typography variant="body2" fontWeight={500}>
                      {row.name}
                    </Typography>
                    {!row.isActive && (
                      <Chip label="Inactive" size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
                    )}
                  </Box>
                </TableCell>

                {sortedDurations.map((duration) => (
                  <Fragment key={duration.id}>
                    {/* Group Price Cell */}
                    <TableCell
                      align="center"
                      sx={{
                        borderRight: '1px dashed',
                        borderColor: 'divider',
                        borderLeft: '2px solid',
                        borderLeftColor: 'divider',
                      }}
                    >
                      <PriceInput
                        rowId={row.id}
                        durationId={duration.id}
                        category="GROUP"
                        priceValues={priceValues}
                        onPriceChange={handlePriceChange}
                      />
                    </TableCell>

                    {/* Individual Price Cell */}
                    <TableCell align="center">
                      <PriceInput
                        rowId={row.id}
                        durationId={duration.id}
                        category="INDIVIDUAL"
                        priceValues={priceValues}
                        onPriceChange={handlePriceChange}
                      />
                    </TableCell>
                  </Fragment>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
